import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Empty, Spin, Tag, Typography } from 'antd';
import { ReloadOutlined, VideoCameraOutlined } from '@ant-design/icons';
import API_BASE_URL from '../../helpers/api/apiConfig';
import type { DeviceVideoStreamItem } from './operationModels';

const { Text } = Typography;

const MAX_STREAM_BUFFER_BYTES = 4 * 1024 * 1024;

const statusColor = (status?: string) => {
    if (status === 'live') return 'green';
    if (status === 'stale') return 'orange';
    if (status === 'waiting') return 'blue';
    if (status === 'disabled') return 'default';
    return 'default';
};

const normalizeStreamUrl = (url: string): string => {
    try {
        const parsed = new URL(url, window.location.origin);
        if (parsed.pathname.startsWith('/devices/') && parsed.pathname.includes('/video-streams/')) {
            return `${API_BASE_URL}${parsed.pathname}${parsed.search}`;
        }
        return parsed.toString();
    } catch {
        return url;
    }
};

const authHeaders = (): HeadersInit => {
    const token = localStorage.getItem('oscar_access_token') || localStorage.getItem('accessToken');
    return token ? { Authorization: `Bearer ${token}` } : {};
};

const findMarker = (buffer: Uint8Array, markerA: number, markerB: number, from = 0) => {
    for (let index = from; index < buffer.length - 1; index += 1) {
        if (buffer[index] === markerA && buffer[index + 1] === markerB) {
            return index;
        }
    }
    return -1;
};

const concatBytes = (left: Uint8Array, right: Uint8Array) => {
    const merged = new Uint8Array(left.length + right.length);
    merged.set(left);
    merged.set(right, left.length);
    return merged;
};

const trimToRecentJpegData = (buffer: Uint8Array) => {
    if (buffer.length <= MAX_STREAM_BUFFER_BYTES) return buffer;
    const cutoff = buffer.length - MAX_STREAM_BUFFER_BYTES;
    const start = findMarker(buffer, 0xff, 0xd8, cutoff);
    if (start >= 0) return buffer.slice(start);
    return buffer.slice(Math.max(0, buffer.length - 1));
};

interface MjpegCameraFeedProps {
    stream: DeviceVideoStreamItem;
}

const MjpegCameraFeed: React.FC<MjpegCameraFeedProps> = ({ stream }) => {
    const [state, setState] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle');
    const [error, setError] = useState('');
    const [frameUrl, setFrameUrl] = useState<string | null>(null);
    const [reloadKey, setReloadKey] = useState(0);
    const lastObjectUrlRef = useRef<string | null>(null);

    const streamUrl = useMemo(() => {
        const url = stream.streamUrl || stream.snapshotUrl;
        if (!url) return null;
        const baseUrl = normalizeStreamUrl(url);
        const separator = baseUrl.includes('?') ? '&' : '?';
        return `${baseUrl}${separator}r=${reloadKey}`;
    }, [reloadKey, stream.snapshotUrl, stream.streamUrl]);

    useEffect(() => {
        if (!streamUrl || !stream.enabled || stream.status === 'disabled') return;

        const controller = new AbortController();
        let cancelled = false;
        let buffer = new Uint8Array();

        const setFrame = (frame: Uint8Array) => {
            const blobUrl = URL.createObjectURL(new Blob([frame], { type: 'image/jpeg' }));
            const previous = lastObjectUrlRef.current;
            lastObjectUrlRef.current = blobUrl;
            setFrameUrl(blobUrl);
            setState('ready');
            setError('');
            if (previous) URL.revokeObjectURL(previous);
        };

        const extractFrames = () => {
            let newestFrame: Uint8Array | null = null;
            while (buffer.length > 0) {
                const start = findMarker(buffer, 0xff, 0xd8);
                if (start < 0) {
                    buffer = buffer.slice(Math.max(0, buffer.length - 1));
                    break;
                }
                const end = findMarker(buffer, 0xff, 0xd9, start + 2);
                if (end < 0) {
                    if (start > 0) buffer = buffer.slice(start);
                    break;
                }
                newestFrame = buffer.slice(start, end + 2);
                buffer = buffer.slice(end + 2);
            }
            if (newestFrame) setFrame(newestFrame);
        };

        const connect = async () => {
            setState('loading');
            setError('');
            try {
                const response = await fetch(streamUrl, {
                    credentials: 'include',
                    headers: authHeaders(),
                    signal: controller.signal,
                });
                if (!response.ok) {
                    throw new Error(`MJPEG stream failed: HTTP ${response.status}`);
                }
                if (!response.body) {
                    const blob = await response.blob();
                    setFrame(new Uint8Array(await blob.arrayBuffer()));
                    return;
                }

                const reader = response.body.getReader();
                while (!cancelled) {
                    const { done, value } = await reader.read();
                    if (done) break;
                    if (value) {
                        buffer = concatBytes(buffer, value);
                        buffer = trimToRecentJpegData(buffer);
                        extractFrames();
                    }
                }
            } catch (err: any) {
                if (!cancelled && err?.name !== 'AbortError') {
                    setState('error');
                    setError(err?.message || 'MJPEG stream unavailable');
                }
            }
        };

        connect();

        return () => {
            cancelled = true;
            controller.abort();
        };
    }, [stream.enabled, stream.status, streamUrl]);

    useEffect(() => {
        return () => {
            if (lastObjectUrlRef.current) {
                URL.revokeObjectURL(lastObjectUrlRef.current);
            }
        };
    }, []);

    return (
        <div style={{ background: '#111827', borderRadius: 6, overflow: 'hidden', border: '1px solid #263244' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 8px' }}>
                <Text style={{ color: '#e5e7eb', fontSize: 11 }}>
                    <VideoCameraOutlined /> {stream.label}
                </Text>
                <span>
                    <ReloadOutlined
                        onClick={() => setReloadKey((value) => value + 1)}
                        style={{ color: '#cbd5e1', fontSize: 11, marginRight: 6, cursor: 'pointer' }}
                    />
                    <Tag color={statusColor(stream.status)} style={{ margin: 0, fontSize: 10 }}>{stream.status}</Tag>
                </span>
            </div>
            {streamUrl && stream.enabled ? (
                <div style={{ position: 'relative', aspectRatio: '16/9', background: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {frameUrl && (
                        <img
                            src={frameUrl}
                            alt={stream.label}
                            style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
                        />
                    )}
                    {state === 'loading' && (
                        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.35)' }}>
                            <Spin />
                        </div>
                    )}
                    {state === 'error' && (
                        <div style={{ position: 'absolute', inset: 0, padding: 8, display: 'flex', alignItems: 'center' }}>
                            <Alert type="warning" message={error || 'Video unavailable'} showIcon style={{ width: '100%' }} />
                        </div>
                    )}
                </div>
            ) : (
                <div style={{ aspectRatio: '16/9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Empty description="No stream configured" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                </div>
            )}
        </div>
    );
};

export default MjpegCameraFeed;
